import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { CodeRefused, type Session } from '../auth';
import {
  CodeNotSent,
  askForCode,
  forgetProven,
  notMe,
  notMeAsThePageCloses,
  notNow,
  provenFor,
  readInvitation,
  sayYes,
  type CodeOnItsWay,
  type FollowUp,
  type InvitationView,
  type LinkRead,
  type YesResult,
} from '../invitation';
import { CODE_LABEL, GREEK_MOBILES_ONLY, INVITATION, TERMS_LINK, athensDay, oneLine } from '../joining';
import { moneyInText } from '../price';
import { appJoinUrl, handedOverFromApp, onAPhone } from '../where';

// E2 step 1 — the invitation entry screen, pre-session by definition:
// /#/join?t={token}. The registered "Supplier invitation" and "SMS code" Entry
// screens; the phone serves the same screens from the app with the same words
// (app/src/shell/Join.tsx). N40:
//
//  - On a phone this page hands the same token to the app — links sent before
//    N40 point here — once: a page handed over never hands back (`via`).
//  - The ink hero leads with the inviting hotel, the person who invited by the
//    name they gave (or just the hotel), and two real numbers from the hotel's
//    own history — never a platform pitch. Then the money line and the terms,
//    accepted with the same tap as the yes (DECISION-080 §4) once George's terms
//    are published.
//  - "Yes, set me up" and "Not now" carry equal prominence (one primary, one
//    secondary of the same size); "This is not me" is smaller, and is held a few
//    seconds with an Undo before it is final — sent as the page closes if it
//    closes during the hold.
//  - The yes texts ONE code to the number the hotel gave — shown partly hidden,
//    changeable; only Greek mobiles get codes for now (DECISION-080 §8). The code
//    proves the phone, makes the account only then (canon 13) and signs them in
//    here — no second code, no second sign-in.
//  - A used link, and an ended one, lead to the ordinary sign-in.

const NOT_ME_HOLD_S = 5;

type Stage = 'invite' | 'code' | 'declined' | 'not-me' | 'burned';

const SignInLink = ({ children }: { children: ReactNode }) => (
  <a href="#/signin" className="btn secondary lg join-signin">
    {children}
  </a>
);

export function Join({ onSignedIn }: { onSignedIn: (s: Session) => void }) {
  const token = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('t') ?? '';
  const onPhone = onAPhone(navigator.userAgent) && !handedOverFromApp();
  useEffect(() => {
    if (onPhone && token) window.location.replace(appJoinUrl(token));
  }, [onPhone, token]);

  const [link, setLink] = useState<LinkRead | null>(null);
  const load = useCallback(async () => setLink(await readInvitation(token)), [token]);
  useEffect(() => {
    if (!onPhone) void load();
  }, [onPhone, load]);

  let body: ReactNode;
  if (!token) {
    body = <Ended title={INVITATION.invalidTitle} line={INVITATION.invalid} />;
  } else if (onPhone || !link) {
    body = (
      <>
        <h1>{INVITATION.oneMoment}</h1>
        <p className="welcome">{INVITATION.opening}</p>
      </>
    );
  } else if (link.kind === 'expired') {
    body = <Ended title={INVITATION.expiredTitle} line={INVITATION.expired(link.hotelName)} />;
  } else if (link.kind === 'invalid') {
    body = <Ended title={INVITATION.invalidTitle} line={INVITATION.invalid} />;
  } else if (link.kind === 'failed') {
    body = (
      <>
        <p className="welcome" role="status">{INVITATION.failed}</p>
        <button className="btn lg" style={{ width: '100%' }} onClick={() => void load()}>
          {INVITATION.retry}
        </button>
      </>
    );
  } else {
    body = <Invitation token={token} inv={link.invitation} reload={load} onSignedIn={onSignedIn} />;
  }

  return (
    <div className="signin-wrap">
      <div className="signin-form-side" style={{ margin: 'auto' }}>
        <div className="signin">{body}</div>
      </div>
    </div>
  );
}

/** A link that cannot be used: what happened — and, for everyone who already joined, the way in. */
function Ended({ title, line }: { title: string; line: string }) {
  return (
    <>
      <h1>{title}</h1>
      <p className="welcome">{line}</p>
      <SignInLink>{INVITATION.alreadyIn}</SignInLink>
    </>
  );
}

function Invitation({
  token,
  inv,
  reload,
  onSignedIn,
}: {
  token: string;
  inv: InvitationView;
  reload: () => Promise<void>;
  onSignedIn: (s: Session) => void;
}) {
  const hotel = inv.hotelName;
  const used = inv.status === 'joined';
  const proven = provenFor(token);
  const [stage, setStage] = useState<Stage>('invite');
  const [sent, setSent] = useState<(CodeOnItsWay & { at: number; sends: number }) | null>(null);
  // A number that cannot get a code (not +30) starts as the field.
  const [editing, setEditing] = useState(!inv.phoneHint || !inv.phoneHint.startsWith('+30'));
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [holdLeft, setHoldLeft] = useState(NOT_ME_HOLD_S);
  const [followUp, setFollowUp] = useState<FollowUp | undefined>(undefined);
  const phoneField = useRef<HTMLInputElement | null>(null);

  const finish = (r: YesResult) => {
    forgetProven(token);
    if (r.kind === 'signed-in') onSignedIn(r.session);
    else {
      // The yes is carried out; only the sign-in did not complete. The link now
      // reads as used, and the ordinary sign-in gets them in.
      setNotice(INVITATION.signInInstead);
      setStage('invite');
      void reload();
    }
  };

  const ask = async (sends: number) => {
    const c = await askForCode(token, { phone: editing ? typed.trim() : undefined, ...(inv.termsVersion ? { termsVersion: inv.termsVersion } : {}) });
    setSent({ ...c, at: Date.now(), sends });
    setStage('code');
  };

  const openNumber = () => {
    setEditing(true);
    requestAnimationFrame(() => phoneField.current?.focus());
  };

  const refused = (e: unknown) => {
    if (e instanceof CodeNotSent) {
      if (e.kind === 'hotel-number') {
        openNumber();
        setError(INVITATION.hotelNumberCannot(hotel));
        return;
      }
      if (e.kind === 'used') {
        setNotice(INVITATION.signInInstead);
        void reload();
        return;
      }
      if (e.kind === 'terms' || e.kind === 'expired' || e.kind === 'gone') void reload();
    }
    setError((e as Error).message);
  };

  const yes = async () => {
    setBusy(true);
    setError('');
    try {
      if (proven) finish(await sayYes(token, { challengeId: proven, ...(inv.termsVersion ? { termsVersion: inv.termsVersion } : {}) }));
      else await ask(1);
    } catch (e) {
      if (e instanceof CodeRefused) {
        forgetProven(token);
        setError(INVITATION.provenExpired);
      } else refused(e);
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    setBusy(true);
    setError('');
    try {
      setFollowUp((await notNow(token)).followUp);
      setStage('declined');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // This is not me: held a few seconds with an Undo, then final — and sent with
  // the page if the tab closes during the hold (N40 review).
  useEffect(() => {
    if (stage !== 'not-me') return;
    const closing = () => {
      if (document.visibilityState === 'hidden') notMeAsThePageCloses(token);
    };
    const gone = () => notMeAsThePageCloses(token);
    document.addEventListener('visibilitychange', closing);
    window.addEventListener('pagehide', gone);
    return () => {
      document.removeEventListener('visibilitychange', closing);
      window.removeEventListener('pagehide', gone);
    };
  }, [stage, token]);
  useEffect(() => {
    if (stage !== 'not-me') return;
    if (holdLeft <= 0) {
      void notMe(token)
        .then(() => setStage('burned'))
        .catch((e: Error) => {
          setError(e.message);
          setStage('invite');
        });
      return;
    }
    const t = window.setTimeout(() => setHoldLeft((k) => k - 1), 1000);
    return () => window.clearTimeout(t);
  }, [stage, holdLeft, token]);

  if (used) {
    // Their yes was carried out: no code from the link, no number shown — the
    // ordinary sign-in (security review S-A).
    return (
      <>
        <h1>{INVITATION.usedTitle}</h1>
        <p className="welcome">{notice || INVITATION.used(hotel)}</p>
        <SignInLink>{INVITATION.signIn}</SignInLink>
      </>
    );
  }

  if (stage === 'code' && sent) {
    return (
      <CodeStep
        token={token}
        sent={sent}
        onChallenge={(challengeId) => setSent({ ...sent, challengeId })}
        resend={() => ask(sent.sends + 1)}
        onDone={finish}
        onChangeNumber={() => {
          setStage('invite');
          openNumber();
        }}
      />
    );
  }
  if (stage === 'declined') {
    return (
      <>
        <h1>{INVITATION.declinedTitle}</h1>
        {/* The reminder line sits in the paragraph that already has the spacing. */}
        <p className="welcome">
          {INVITATION.declined(hotel)}
          {followUp ? ` ${INVITATION.followUpLine(athensDay(followUp.at), followUp.by, followUp.simulated)}` : ''}
        </p>
        <button className="btn secondary lg" style={{ width: '100%' }} onClick={() => setStage('invite')}>
          {INVITATION.changedMind}
        </button>
      </>
    );
  }
  if (stage === 'burned') return <Ended title={INVITATION.burnedTitle} line={INVITATION.burned(hotel)} />;
  if (stage === 'not-me') {
    return (
      <>
        <p className="welcome" aria-hidden="true">
          {INVITATION.notMePending(hotel, holdLeft)}
        </p>
        <p className="sr-only" role="status">
          {INVITATION.notMeStatus(hotel)}
        </p>
        <button
          className="btn lg"
          style={{ width: '100%' }}
          onClick={() => {
            setHoldLeft(NOT_ME_HOLD_S);
            setStage('invite');
          }}
        >
          {INVITATION.undo}
        </button>
      </>
    );
  }

  const numberBlock = proven ? (
    <p className="hint">{INVITATION.proven}</p>
  ) : editing ? (
    <div className="field">
      <label htmlFor="jphone">{INVITATION.numberLabel}</label>
      <input
        id="jphone"
        ref={phoneField}
        className="mono"
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        enterKeyHint="send"
        placeholder="+30 691 234 5678"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
      />
      <p className="hint" style={{ marginTop: 'var(--space-xs)' }}>
        {GREEK_MOBILES_ONLY}
      </p>
    </div>
  ) : (
    <p className="hint">
      {/* DECISION-080 §8: said on the join page whichever number it is. */}
      <span aria-label={INVITATION.hintLabel(inv.phoneHint!)}>{INVITATION.codeTo(oneLine(inv.phoneHint!))}</span> {GREEK_MOBILES_ONLY}{' '}
      <button type="button" className="btn ghost lg join-other" disabled={busy} onClick={openNumber}>
        {INVITATION.otherNumber}
      </button>
    </p>
  );

  const errorLine = error && (
    <p className="error-line" role="alert">
      {error}
    </p>
  );

  return (
    <>
      <div className="hero" style={{ marginBottom: 'var(--space-lg)' }}>
        <div className="eyebrow">{INVITATION.invitedBy(inv.invitedBy, hotel)}</div>
        <div className="join-name">{inv.businessName}</div>
        <div className="join-blurb">
          {inv.history
            ? INVITATION.history(hotel, inv.history.bookingsSettled, moneyInText(inv.history.typicalAmount.minor, inv.history.typicalAmount.currency))
            : INVITATION.noHistory(hotel)}
        </div>
      </div>
      {notice && (
        <p className="hint" role="status">
          {notice}
        </p>
      )}
      <p className="welcome" style={{ marginTop: 'var(--space-md)' }}>
        {INVITATION.plan}
      </p>
      <p className="welcome" style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)' }}>
        {INVITATION.money(hotel)}
      </p>
      <p className="hint">
        {INVITATION.termsBefore}
        <a href={TERMS_LINK.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-link)' }}>
          {TERMS_LINK.label}
        </a>
        {INVITATION.termsAfter}
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void yes();
        }}
      >
        {numberBlock}
        {errorLine}
        <div style={{ display: 'flex', gap: 'var(--space-sm)', marginTop: 'var(--space-lg)' }}>
          <button type="submit" className="btn lg" style={{ flex: 1 }} disabled={busy || (editing && !proven && !typed.trim())}>
            {busy ? INVITATION.sending : INVITATION.yes}
          </button>
          <button type="button" className="btn secondary lg" style={{ flex: 1 }} disabled={busy} onClick={decline}>
            {INVITATION.notNow}
          </button>
        </div>
      </form>
      <button
        type="button"
        className="btn ghost lg join-notme"
        disabled={busy}
        onClick={() => {
          setHoldLeft(NOT_ME_HOLD_S);
          setStage('not-me');
        }}
      >
        {INVITATION.notMe}
      </button>
    </>
  );
}

function CodeStep({
  token,
  sent,
  onChallenge,
  resend,
  onDone,
  onChangeNumber,
}: {
  token: string;
  sent: CodeOnItsWay & { at: number; sends: number };
  onChallenge: (challengeId: string) => void;
  resend: () => Promise<void>;
  onDone: (r: YesResult) => void;
  onChangeNumber: () => void;
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [finished, setFinished] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const checking = useRef(false);

  const check = async (full: string) => {
    if (checking.current) return;
    checking.current = true;
    setBusy(true);
    setError('');
    try {
      onDone(await sayYes(token, { challengeId: sent.challengeId, code: full }));
    } catch (e) {
      setCode('');
      if (e instanceof CodeRefused) {
        setError(e.message);
        if (e.kind === 'wrong') onChallenge(e.challengeId);
        else setFinished(true);
      } else setError((e as Error).message);
    } finally {
      checking.current = false;
      setBusy(false);
    }
  };

  const waitLeft = Math.max(0, Math.ceil((sent.at + sent.resendAfterSeconds * 1000 - now) / 1000));
  const counting = !finished && waitLeft > 0;
  useEffect(() => {
    if (!counting) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [counting]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void check(code);
      }}
    >
      <h1>{INVITATION.codeTitle}</h1>
      {/* The number once, in the API's own line. */}
      <p className="welcome" role="status">
        {sent.message}
      </p>
      <div className="field">
        <label htmlFor="code">{CODE_LABEL}</label>
        {/* The code input's height and spacing are the sign-in's own (SignIn.tsx) — copied, not new. */}
        <input
          id="code"
          inputMode="numeric"
          pattern="[0-9]{6}"
          autoComplete="one-time-code"
          value={code}
          disabled={finished}
          autoFocus
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
            setCode(digits);
            if (digits.length === 6) void check(digits);
          }}
          className="mono"
          style={{ fontSize: 'var(--text-2xl)', letterSpacing: '0.4em', textAlign: 'center', height: 52 }}
        />
      </div>
      {error && (
        <p className="error-line" role="alert">
          {error}
        </p>
      )}
      <button className={finished ? 'btn secondary lg' : 'btn lg'} disabled={busy || code.length !== 6 || finished} style={{ width: '100%' }}>
        {busy ? INVITATION.checking : INVITATION.continue}
      </button>
      {finished || waitLeft === 0 ? (
        <button
          type="button"
          className={finished ? 'btn lg' : 'btn ghost'}
          style={{ width: '100%', marginTop: 'var(--space-sm)' }}
          disabled={busy}
          onClick={() => {
            setError('');
            void resend()
              .then(() => {
                setFinished(false);
                setCode('');
                setNow(Date.now());
              })
              .catch((e: Error) => setError(e.message));
          }}
        >
          {INVITATION.sendAgain}
        </button>
      ) : (
        <p className="hint" style={{ marginTop: 'var(--space-sm)' }}>
          {INVITATION.sendAgainIn(waitLeft)}
        </p>
      )}
      <button type="button" className="btn ghost lg join-other" style={{ marginTop: 'var(--space-md)' }} onClick={onChangeNumber}>
        {INVITATION.changeNumber}
      </button>
    </form>
  );
}
